import {
  MEMBERSHIP_30_CURRENT,
  MEMBERSHIP_45_CURRENT,
  MEMBERSHIP_60_CURRENT,
  MEMBERSHIP_30_NEW,
  MEMBERSHIP_45_NEW,
  MEMBERSHIP_60_NEW,
  PRICE_INCREASE_DATE,
  MEMBERSHIP_MIGRATION_DATE,
  membershipIncreaseIsLive,
  membershipRates,
} from './membershipRates.js';

// Single source of truth for trial + membership pricing.
// Numeric membership rates and the Sept 1 / Jan 1 gates live in membershipRates.js
// (no React) so the page and Clara import the same clock.
// Special trial prices are stored outright ($40 / $60 / $72 by length). Opus1
// charges those amounts directly — there is no checkout promo or percent off.
// Struck-through figures are the current regular per-lesson prices
// ($58 / $88 / $105), also stored outright, not derived from a percentage.
// Public copy (locked 2026-09-08): never market the trial as "20% off" / "% off".
// Use promoHeadline — "Special trial price from $40" — or a close "from $40" variant.
// If a trial price or a regular per-lesson price changes, update this file only.

export const formatPrice = (n) => {
  return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`;
};

// Regular per-lesson prices (struck through on the site) and special trial
// prices (what the student pays). Both are dollar amounts in this table.
// `original` is the regular per-lesson price. `discounted` is the trial price;
// the field name is historical and is not a discount off `original`.
export const trialTiers = [
  { duration: '60 Minutes', original: 105, discounted: 72, note: null },
  { duration: '45 Minutes', original: 88, discounted: 60, note: null, popular: true },
  { duration: '30 Minutes', original: 58, discounted: 40, note: 'Ages 5 and up' },
].map((t) => ({
  ...t,
  originalLabel: formatPrice(t.original),
  discountedLabel: formatPrice(t.discounted),
}));

export const trialFromOriginal = trialTiers[trialTiers.length - 1].original;
export const trialFromDiscounted = trialTiers[trialTiers.length - 1].discounted;
export const trialFromOriginalLabel = formatPrice(trialFromOriginal);
export const trialFromDiscountedLabel = formatPrice(trialFromDiscounted);
// "$40 / $60 / $72" — shortest lesson first. Plain trial prices, no percent off.
export const trialPriceListLabel = [...trialTiers]
  .sort((a, b) => a.discounted - b.discounted)
  .map((t) => t.discountedLabel)
  .join(' / ');
// 45-minute regular per-lesson price. /expo's struck "regular" price reads this
// so it cannot drift from the pricing table.
export const trial45RegularLabel = trialTiers.find((t) => t.duration === '45 Minutes').originalLabel;

// Locked public trial promo line (2026-09-08). Do not interpolate a % off here.
export const promoHeadline = `Special trial price from ${trialFromDiscountedLabel}`;
// Public eyebrow / pill next to strike-through prices. Same locked line — never
// "New Student Promo", "% off", or other discount-percentage framing.
export const promoSublabel = promoHeadline;

// Canonical Opus1 trial + lead URLs. Checkout charges the trial prices above
// directly. These URLs still include promoId; do not rewrite the links here.
// Centralize so we never have to grep-and-replace these again. (vercel.json
// redirects still carry the literal — JSON can't import.)
export const TRIAL_PROMO_ID = '2cc064f7-e065-4dbe-9544-7cfe709ca763';
export const TRIAL_PROMO_QUERY = `?promoId=${TRIAL_PROMO_ID}`;
// Append promoId to any Opus1 trial URL, ?/& aware (slug may carry a query).
// The query stays on the URL for now; it does not apply a checkout discount.
export const withTrialPromo = (url) =>
  url.includes('promoId=') ? url : `${url}${url.includes('?') ? '&' : '?'}promoId=${TRIAL_PROMO_ID}`;
export const TRIAL_URL = `https://sflcm.opus1.io/w/trial${TRIAL_PROMO_QUERY}`;
export const LEAD_URL = 'https://sflcm.opus1.io/lead';

// Canonical instrument-specific Opus1 trial routes. Keep this list intentionally
// narrow: these are the six routes verified in the existing instrument pages.
// Viola is not included until its dedicated Opus1 route is confirmed.
//
// THIS LIST IS MARK'S CONTROL POINT for the instrument-first trial flow
// (the /trial page + every "Book a Trial Lesson" chooser site-wide):
//   - Order here = display order (demand-first, piano leads).
//   - `enabled: false` hides an instrument from every chooser surface without
//     touching any component — one-line edit + deploy. Direct per-instrument
//     links (instrument pages, /lp/*, teacher pages) are NOT affected.
const instrumentTrial = (slug, label, opus1Path, { enabled = true } = {}) => Object.freeze({
  slug,
  label,
  enabled,
  url: withTrialPromo(`https://sflcm.opus1.io/w/${opus1Path}`),
});

export const INSTRUMENT_TRIALS = Object.freeze([
  instrumentTrial('piano', 'Piano', 'trialpiano'),
  instrumentTrial('violin', 'Violin', 'trialviolin'),
  instrumentTrial('guitar', 'Guitar', 'trialguitar'),
  instrumentTrial('voice', 'Voice/Singing', 'trialsinging'),
  instrumentTrial('drums', 'Drums', 'trialdrums'),
  instrumentTrial('cello', 'Cello', 'trialcello'),
]);

// Chooser surfaces render only enabled instruments. Exposed as a helper so the
// enabled-flag behavior is directly testable against fixtures.
export const enabledInstrumentTrials = (list = INSTRUMENT_TRIALS) =>
  list.filter((instrument) => instrument.enabled !== false);

export const ENABLED_INSTRUMENT_TRIALS = Object.freeze(enabledInstrumentTrials());

export const INSTRUMENT_TRIAL_BY_SLUG = Object.freeze(
  Object.fromEntries(INSTRUMENT_TRIALS.map((instrument) => [instrument.slug, instrument]))
);

export const getInstrumentTrialUrl = (slug) =>
  INSTRUMENT_TRIAL_BY_SLUG[slug]?.url || TRIAL_URL;

// ── Monthly membership tuition ──────────────────────────────────────────────
// The 2026 price increase (locked 2026-07-05): rates rise on Sept 1, 2026.
// Families who enroll on or before Aug 31, 2026 keep CURRENT rates through
// Dec 31, 2026; on Jan 1, 2027 every membership moves to the new rates.
//
// To flip the whole site to new rates, edit src/data/membershipRates.js only — not JSX
// and not api/chat.js. The pricing page, promo module, FAQ, JSON-LD, dated
// clarifiers, and Clara all read that module. On Sept 1 the cards flip
// automatically, the lock-in promo auto-hides; on Jan 1 2027 the transition
// copy auto-retires.

export {
  MEMBERSHIP_30_CURRENT,
  MEMBERSHIP_45_CURRENT,
  MEMBERSHIP_60_CURRENT,
  MEMBERSHIP_30_NEW,
  MEMBERSHIP_45_NEW,
  MEMBERSHIP_60_NEW,
  PRICE_INCREASE_DATE,
  MEMBERSHIP_MIGRATION_DATE,
  membershipIncreaseIsLive,
  membershipRates,
};

// Page-only lock-in date. The increase and migration gates live in membershipRates.js.
//   - LOCKIN_ENROLL_BY: last day to enroll and keep current rates (Aug 31).
export const LOCKIN_ENROLL_BY = new Date(2026, 7, 31);          // 2026-08-31

// Formatted date strings for copy (kept here so surfaces never re-hardcode them).
export const PRICE_INCREASE_DATE_LABEL = 'September 1, 2026';
export const LOCKIN_ENROLL_BY_LABEL = 'August 31';
export const LOCKIN_ENROLL_BY_LABEL_FULL = 'August 31, 2026';
export const LOCKIN_HOLDS_THROUGH_LABEL = 'December 31, 2026';
export const MEMBERSHIP_MIGRATION_DATE_LABEL = 'January 1, 2027';

// The lock-in promo (and "enrolled in August" transition copy) shows only until
// the increase lands; after Jan 1, 2027 all transition copy is retired.
export const lockInPromoIsActive = (now = new Date()) => now < PRICE_INCREASE_DATE;
export const transitionCopyIsActive = (now = new Date()) => now < MEMBERSHIP_MIGRATION_DATE;

const _rates = membershipRates();

// Pre-formatted membership price labels for the current date ("$199" / "$229" …).
export const MEMBERSHIP_30_LABEL = formatPrice(_rates.min30);
export const MEMBERSHIP_45_LABEL = formatPrice(_rates.min45);
export const MEMBERSHIP_60_LABEL = formatPrice(_rates.min60);

// New-rate labels (always the post-increase figures) for transition/promo copy.
export const MEMBERSHIP_30_NEW_LABEL = formatPrice(MEMBERSHIP_30_NEW);
export const MEMBERSHIP_45_NEW_LABEL = formatPrice(MEMBERSHIP_45_NEW);
export const MEMBERSHIP_60_NEW_LABEL = formatPrice(MEMBERSHIP_60_NEW);

// "From" (lowest, 30-min) membership price for the current date.
export const MEMBERSHIP_FROM = _rates.min30;
export const MEMBERSHIP_FROM_LABEL = formatPrice(MEMBERSHIP_FROM);

// ── Summer lock-in strike-through framing ───────────────────────────────────
// During the lock-in window (now → Aug 31, 2026) the membership cards frame the
// increase as a deal: the NEW rate is struck and dated, the CURRENT rate is the
// prominent figure a family pays today. After Sept 1 the increase is live, the
// current rate is gone, and cards render the new rate plain (no strike).
//
// `membershipShowStrike()` gates the JSX; the struck figure is ALWAYS dated with
// LOCKIN_STRUCK_FROM_LABEL so it reads "goes up Sept 1", never a bare sale slash.
export const membershipShowStrike = (now = new Date()) => lockInPromoIsActive(now);

// Short adjacent label for the struck (future) price — "from September 1"
// (drops the trailing ", 2026" year from the full increase-date label).
export const LOCKIN_STRUCK_FROM_LABEL = `from ${PRICE_INCREASE_DATE_LABEL.replace(/,\s*\d{4}$/, '')}`;

// Per-tier current (prominent) + new (struck) labels for the cards. Cards read
// these so no membership number is ever hardcoded in JSX.
export const MEMBERSHIP_STRIKE_TIERS = {
  min30: { current: formatPrice(MEMBERSHIP_30_CURRENT), struck: MEMBERSHIP_30_NEW_LABEL },
  min45: { current: formatPrice(MEMBERSHIP_45_CURRENT), struck: MEMBERSHIP_45_NEW_LABEL },
  min60: { current: formatPrice(MEMBERSHIP_60_CURRENT), struck: MEMBERSHIP_60_NEW_LABEL },
};

// "From" (30-min) current price — the prominent figure for single-price surfaces
// (e.g. LessonsPage "from $199/mo") during the lock-in window.
export const MEMBERSHIP_FROM_CURRENT_LABEL = formatPrice(MEMBERSHIP_30_CURRENT);
export const MEMBERSHIP_FROM_NEW_LABEL = MEMBERSHIP_30_NEW_LABEL;

// Registration fees (one-time at sign-up).
export const REGISTRATION_FEE_NEW = 59;
export const REGISTRATION_FEE_RETURNING = 39;

// Family discount: every additional membership (a sibling, or a second
// instrument for the same student) gets this much off its tuition.
export const FAMILY_DISCOUNT = 0.05;
export const FAMILY_DISCOUNT_LABEL = '5%';
export const REGISTRATION_FEE_NEW_LABEL = formatPrice(REGISTRATION_FEE_NEW);
export const REGISTRATION_FEE_RETURNING_LABEL = formatPrice(REGISTRATION_FEE_RETURNING);
